/**
 * UTM Parameter Tracking Utility for PNGtoSVG
 * 
 * Simple utility to append UTM parameters to links
 */

/**
 * Get UTM parameters from current page URL
 * @returns {Object} UTM parameters object
 */
export const getUTMParams = () => {
  const params = {};
  const urlParams = new URLSearchParams(window.location.search);
  
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  utmParams.forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      params[param] = value;
    }
  });
  
  return params;
};

/**
 * Store UTM parameters in sessionStorage and localStorage
 */
export const storeUTMParams = () => {
  const utmParams = getUTMParams();
  if (Object.keys(utmParams).length === 0) {
    return; // No UTM params to store
  }

  // Store in sessionStorage (cleared when tab closes)
  try {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    console.log('✅ UTM parameters stored in sessionStorage:', utmParams);
  } catch (e) {
    console.warn('⚠️ Could not store UTM params in sessionStorage:', e);
  }

  // Store in localStorage for first-touch and last-touch attribution
  try {
    // First touch - only store if doesn't exist
    const existingFirstTouch = localStorage.getItem('utm_first_touch');
    if (!existingFirstTouch) {
      const firstTouch = {
        ...utmParams,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('utm_first_touch', JSON.stringify(firstTouch));
      console.log('✅ First touch UTM stored:', firstTouch);
    }

    // Last touch - always update
    const lastTouch = {
      ...utmParams,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('utm_last_touch', JSON.stringify(lastTouch));
    console.log('✅ Last touch UTM stored:', lastTouch);
  } catch (e) {
    console.warn('⚠️ Could not store UTM params in localStorage:', e);
  }
};

/**
 * Append UTM parameters to a URL
 * @param {string} url - Base URL
 * @param {string} utmContent - Optional utm_content value to track specific CTA/action
 * @returns {string} URL with UTM parameters appended
 */
export const appendUTMToURL = (url, utmContent = null) => {
  if (!url) return url;
  
  // Get stored UTM params from sessionStorage first, fallback to URL params
  let utmParams = {};
  try {
    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      utmParams = JSON.parse(stored);
    }
  } catch (e) {
    // Fallback to URL params if sessionStorage fails
    utmParams = getUTMParams();
  }
  
  // If no stored params, get from URL
  if (Object.keys(utmParams).length === 0) {
    utmParams = getUTMParams();
  }
  
  // Add utm_content if provided (for tracking specific CTAs)
  if (utmContent) {
    utmParams.utm_content = utmContent;
  }
  
  if (Object.keys(utmParams).length === 0) {
    return url;
  }
  
  try {
    const urlObj = new URL(url);
    Object.keys(utmParams).forEach(key => {
      urlObj.searchParams.set(key, utmParams[key]);
    });
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails (e.g., relative URL), append manually
    const separator = url.includes('?') ? '&' : '?';
    const paramString = Object.keys(utmParams)
      .map(key => `${key}=${encodeURIComponent(utmParams[key])}`)
      .join('&');
    
    return paramString ? `${url}${separator}${paramString}` : url;
  }
};

